/**
 * Times are always shown in the office's timezone, never the visitor's, so a
 * patient travelling or on a misconfigured phone still sees the office clock.
 */

const cache = new Map<string, Intl.DateTimeFormat>();
function fmt(tz: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${tz}|${JSON.stringify(opts)}`;
  let f = cache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: tz, ...opts });
    cache.set(key, f);
  }
  return f;
}

/** "9:40 AM". */
export function timeLabel(iso: string, tz: string): string {
  return fmt(tz, { hour: 'numeric', minute: '2-digit' }).format(new Date(iso));
}

/** Office-local date `YYYY-MM-DD` of an instant. */
export function localDate(iso: string, tz: string): string {
  const parts = fmt(tz, { year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(
    new Date(iso),
  );
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Office-local minutes since midnight of an instant. */
export function localMinutes(iso: string, tz: string): number {
  const parts = fmt(tz, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(
    new Date(iso),
  );
  const h = Number(parts.find((p) => p.type === 'hour')?.value ?? 0);
  const m = Number(parts.find((p) => p.type === 'minute')?.value ?? 0);
  return h * 60 + m;
}

const noonUtc = (date: string) => new Date(`${date}T12:00:00Z`);

/** "Tue", "Oct 14" style parts for a local date. */
export function dayParts(date: string): { dow: string; month: string; day: string } {
  const d = noonUtc(date);
  return {
    dow: new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: 'UTC' }).format(d),
    month: new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' }).format(d),
    day: String(d.getUTCDate()),
  };
}

/** "Today", "Tomorrow" or "Tuesday, October 14". */
export function dayLabel(date: string, today: string): string {
  if (date === today) return 'Today';
  if (date === addDays(today, 1)) return 'Tomorrow';
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(noonUtc(date));
}

/** "Tuesday, October 14 at 9:40 AM". */
export function whenLabel(iso: string, tz: string, today: string): string {
  return `${dayLabel(localDate(iso, tz), today)} at ${timeLabel(iso, tz)}`;
}

export function addDays(date: string, n: number): string {
  const d = noonUtc(date);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Part of the day for grouping the time list. */
export function partOfDay(minutes: number): 'Morning' | 'Afternoon' | 'Evening' {
  return minutes < 12 * 60 ? 'Morning' : minutes < 17 * 60 ? 'Afternoon' : 'Evening';
}

/** "45 min" or "1 hr 30 min". */
export function lengthLabel(min: number): string {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}

/** As-you-type US phone: "(989) 555-0142". */
export function formatPhoneInput(raw: string): string {
  let d = raw.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  d = d.slice(0, 10);
  if (d.length <= 3) return d.length ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** A valid 10-digit US mobile (area code and exchange not starting 0 or 1). */
export function isMobile(raw: string): boolean {
  let d = raw.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(d);
}

/** "tel:+19895550190" for a display number. */
export function telHref(phone: string): string {
  let d = phone.replace(/\D/g, '');
  if (d.length === 10) d = `1${d}`;
  return `tel:+${d}`;
}

/** As-you-type date of birth "MM/DD/YYYY". */
export function formatDobInput(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/**
 * "MM/DD/YYYY" to ISO, or null when it is not a real date in the past
 * 120 years (relative to `today`).
 */
export function parseDob(text: string, today: string): string | null {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text.trim());
  if (!m) return null;
  const [mo, da, yr] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const d = new Date(Date.UTC(yr, mo - 1, da, 12));
  if (d.getUTCFullYear() !== yr || d.getUTCMonth() !== mo - 1 || d.getUTCDate() !== da) {
    return null;
  }
  const iso = d.toISOString().slice(0, 10);
  if (iso > today) return null;
  if (Number(today.slice(0, 4)) - yr > 120) return null;
  return iso;
}

/** ISO date to "MM/DD/YYYY". */
export function dobText(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

/** Whole years between a birthdate and a local date. */
export function ageOn(birthdate: string, onDate: string): number {
  const [by, bm, bd] = birthdate.split('-').map(Number) as [number, number, number];
  const [y, m, d] = onDate.split('-').map(Number) as [number, number, number];
  return y - by - (m < bm || (m === bm && d < bd) ? 1 : 0);
}

/**
 * Whether a person born on `birthdate` fits a visit type's age band on
 * `onDate` (adults are 18 and over). Unknown birthdates fit.
 */
export function fitsAgeBand(
  band: 'all' | 'adult' | 'child',
  birthdate: string | null,
  onDate: string,
): boolean {
  if (band === 'all' || !birthdate) return true;
  const age = ageOn(birthdate, onDate);
  return band === 'adult' ? age >= 18 : age < 18;
}

/** "4:59" for a countdown in seconds. */
export function countdown(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
